import { Tier } from "../types/user";
import { chatConfig } from "../config/chatConfig";

export type Currency = 'usd' | 'cad' | 'eur';
export type Interval = 'monthly' | 'annual';

type PriceTable = Record<Currency, { monthly: number; annual: number }>;

export interface PlanLimits {
    messages: number;
    images: number;
    filesPerChat: number;
    spaces: boolean;
    betaFeatures: boolean;
}

export interface PricingTier {
    name: string;
    idname: Tier;
    tagline: string;
    highlight: boolean;
    price: PriceTable;
    limits: PlanLimits;
    features: string[];
    // Checkout grants this trial on every paid plan when no promo code is used
    // (see STUDYBUDDY-API handlers/Stripe/payments.ts).
    trialDays?: number;
}

export const TRIAL_DAYS = 7;

// Limits mirror the API (utils/rateLimit.ts) and config/chatConfig.ts.
const LIMITS: Record<Tier, PlanLimits> = {
    free: { messages: 150, images: 10, filesPerChat: chatConfig.limits.files.MAX_FILES_PER_CHAT.free, spaces: false, betaFeatures: false },
    premium: { messages: 1500, images: 75, filesPerChat: chatConfig.limits.files.MAX_FILES_PER_CHAT.premium, spaces: false, betaFeatures: false },
    ultimate: { messages: 2500, images: 150, filesPerChat: chatConfig.limits.files.MAX_FILES_PER_CHAT.ultimate, spaces: true, betaFeatures: true },
};

const fmt = (n: number) => n.toLocaleString('en-US');

export const pricingTiers: PricingTier[] = [
    {
        name: 'Free',
        idname: 'free',
        tagline: 'Try StudyBuddy with everyday limits.',
        highlight: false,
        price: {
            usd: { monthly: 0, annual: 0 },
            cad: { monthly: 0, annual: 0 },
            eur: { monthly: 0, annual: 0 },
        },
        limits: LIMITS.free,
        features: [
            `${fmt(LIMITS.free.messages)} Messages a Month`,
            `${LIMITS.free.images} Image Generations a Month`,
            `Up to ${LIMITS.free.filesPerChat} File Uploads per Chat`,
            'Quiz Generation',
        ],
    },
    {
        name: 'Premium',
        idname: 'premium',
        tagline: 'More room for everyday use.',
        highlight: false,
        trialDays: TRIAL_DAYS,
        price: {
            usd: { monthly: 5.59, annual: 49.99 },
            cad: { monthly: 6.99, annual: 59.99 },
            eur: { monthly: 5.59, annual: 49.99 },
        },
        limits: LIMITS.premium,
        features: [
            `${fmt(LIMITS.premium.messages)} Messages a Month`,
            `${LIMITS.premium.images} Image Generations a Month`,
            `Up to ${LIMITS.premium.filesPerChat} File Uploads per Chat`,
            'Quiz Generation',
        ],
    },
    {
        name: 'Ultimate',
        idname: 'ultimate',
        tagline: 'Everything, with the highest limits.',
        highlight: true,
        trialDays: TRIAL_DAYS,
        price: {
            usd: { monthly: 8.99, annual: 89.99 },
            cad: { monthly: 9.99, annual: 99.99 },
            eur: { monthly: 8.99, annual: 89.99 },
        },
        limits: LIMITS.ultimate,
        features: [
            `${fmt(LIMITS.ultimate.messages)} Messages a Month`,
            `${LIMITS.ultimate.images} Image Generations a Month`,
            `Up to ${LIMITS.ultimate.filesPerChat} File Uploads per Chat`,
            'Spaces for Projects and Topics',
            'Early Access to Beta Features',
        ],
    },
];

export const getTier = (id: string | null | undefined) =>
    pricingTiers.find(t => t.idname === id?.toLowerCase());

export const isPaidTier = (id: string | null | undefined): id is 'premium' | 'ultimate' =>
    id === 'premium' || id === 'ultimate';

export const CURRENCY_LABEL: Record<Currency, string> = { usd: 'USD', cad: 'CAD', eur: 'EUR' };

export const formatMoney = (amount: number, currency: Currency) =>
    new Intl.NumberFormat(currency === 'cad' ? 'en-CA' : currency === 'eur' ? 'en-IE' : 'en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);

/** Price shown per month for the chosen interval. */
export const monthlyEquivalent = (tier: PricingTier, currency: Currency, interval: Interval) => {
    const p = tier.price[currency];
    return interval === 'annual' ? p.annual / 12 : p.monthly;
};

/** Whole-percent saved by paying annually versus twelve monthly payments. */
export const annualSavingsPercent = (tier: PricingTier, currency: Currency) => {
    const p = tier.price[currency];
    if (!p.monthly) return 0;
    return Math.floor((1 - p.annual / (p.monthly * 12)) * 100);
};

export const maxAnnualSavingsPercent = (currency: Currency) =>
    Math.max(...pricingTiers.map(t => annualSavingsPercent(t, currency)));
